import React, { Component } from "react";
import { Link } from "react-router-dom";
import publicApi from "../../utils/publicApi";
import Loading from "./Loading";
import { formatDate } from "../../utils/dateUtils";
import { generateGradient } from "../../utils/colorUtils";
import { isPublicArticle } from "../../utils/articleVisibility";

export default class ArticlesGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      articles: [],
      loading: true,
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: props.itemsPerPage || 12
    };
  }

  componentDidMount() {
    this.loadArticles();
  }

  componentDidUpdate(prevProps) {
    if (
      (prevProps.type !== this.props.type) ||
      (prevProps.featured !== this.props.featured) ||
      (prevProps.session !== this.props.session) ||
      (prevProps.itemsPerPage !== this.props.itemsPerPage)
    ) {
      this.setState({ currentPage: 1 }, () => {
        this.loadArticles();
      });
    }
  }

  loadArticles = async () => {
    try {
      this.setState({ loading: true });

      const { type, featured, itemsPerPage } = this.props;
      const { currentPage } = this.state;
      const limit = itemsPerPage || 12;
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams();
      // Ne pas ajouter le paramètre type si undefined (pour afficher tous les types)
      if (type) {
        params.append("type", type);
      }
      if (featured === true) {
        params.append("featured", "true");
      }
      params.append("limit", limit);
      params.append("offset", offset);

      const response = await publicApi.get(`/api/articles?${params.toString()}`);

      const articles = (response.data.articles || []).filter(isPublicArticle);
      const total = response.data.pagination?.total || 0;
      const totalPages = Math.ceil(total / limit);

      this.setState({
        articles,
        totalPages,
        loading: false
      });
    } catch (error) {
      console.error("Error loading articles:", error);
      this.setState({ loading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, () => {
      this.loadArticles();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  render() {
    const { articles, loading, currentPage, totalPages } = this.state;
    const { emptyMessage = "Aucun article trouvé" } = this.props;

    if (loading) {
      return (
        <Loading variant="spinner" size="medium" color="primary" text="Chargement des articles..." />
      );
    }

    if (articles.length === 0) {
      return (
        <div className="text-center py-5">
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <>
        <div className="articles-grid-container">
          <div className="row">
            {articles.map((article) => {
              const color = article.color || "#e45447";
              const hasImage = article.imageUrl && article.imageUrl.trim() !== "";

              return (
                <div className="col-lg-4 col-md-6 mb-4" key={article.id}>
                  <Link to={`/article/${article.slug}`} className="article-card-link">
                    <div
                      className="article-card"
                      style={{
                        backgroundColor: color,
                        // Le dégradé de secours est posé EN DERNIER : si l'image
                        // 404, la couche url() est vide et le dégradé apparaît
                        // (au lieu d'un aplat de couleur).
                        backgroundImage: hasImage
                          ? `linear-gradient(to top, ${color} 0%, ${color} 40%, transparent 100%), url(${article.imageUrl}), ${generateGradient(color, -20)}`
                          : generateGradient(color, -20),
                        backgroundSize: hasImage ? "cover" : "100%",
                        backgroundPosition: hasImage ? "center" : "center",
                        backgroundRepeat: "no-repeat"
                      }}
                    >
                      <div className="article-card-content">
                        {article.featured && (
                          <span className="article-featured-badge">
                            <i className="lnr lnr-star"></i> À la une
                          </span>
                        )}
                        <h3 className="article-card-title">{article.title}</h3>
                        {article.excerpt && (
                          <p className="article-card-excerpt">{article.excerpt}</p>
                        )}
                        <div className="article-card-meta">
                          {article.publishedAt && (
                            <span className="article-card-date">
                              <i className="lnr lnr-calendar-full"></i> {formatDate(article.publishedAt)}
                            </span>
                          )}
                          {article.author && (
                            <span className="article-card-author">
                              <i className="lnr lnr-user"></i> {article.author.name || article.author.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="articles-pagination">
            <nav aria-label="Pagination des articles">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="lnr lnr-chevron-left"></i> Précédent
                  </button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Afficher seulement les pages proches de la page actuelle
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => this.handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return (
                      <li key={page} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return null;
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Suivant <i className="lnr lnr-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </>
    );
  }
}

